import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { buildTree, followUpAnswers, readVisibility, type FieldNode } from '@lib/customization/poiDesign';
import { type Container, type Draft } from '@lib/customization/poiDraft';
import { useLocalization } from '@lib/localization';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  type FormInputType, type PoiField, type PoiFieldCondition, type PoiFieldGroup, type ResolvedItem,
} from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import { CodeBranchIcon, CopyIcon, GripVerticalIcon, LockIcon, PlusIcon, TrashIcon } from 'react-line-awesome';
import { ActionMenu, TypeMenu, type MenuAnchor, type MenuTarget } from './CanvasMenus';
import {
  addButtonStyles, branchLabelStyles, branchStyles, cardActionsStyles, conditionLineStyles, deviceStyles,
  fieldCardSelectedStyles, fieldCardStyles, groupHeaderStyles, handleStyles, inheritedRegionStyles,
  sortableHoverStyles, warningLineStyles,
} from './designer.styles';
import { branchWords, entryWarnings, ruleWords, whenWords } from './describe';
import { LabelText, type Languages } from './FieldHeading';
import FieldSketch from './FieldSketch';

export type Selection = { kind: 'field' | 'group'; id: string } | null;

/** Everything the canvas can ask of the designer. */
export interface CanvasActions {
  select: (selection: Selection) => void;
  addField: (container: Container, type: FormInputType) => void;
  addGroup: () => void;
  /** `when` is the whole condition of the sibling the new follow-up is added beside, when there is one. */
  addFollowUp: (
    triggerId: string, condition: PoiFieldCondition, type: FormInputType,
    when?: { match: 'all' | 'any'; conditions: PoiFieldCondition[] },
  ) => void;
  insertAfter: (id: string, type: FormInputType) => void;
  duplicate: (id: string) => void;
  remove: (id: string) => void;
  removeGroup: (id: string) => void;
  moveBlock: (container: Container, from: number, to: number) => void;
  moveGroup: (from: number, to: number) => void;
}

interface Props {
  draft: Draft;
  /** Ancestors' content from `/effective`, drawn locked above the type's own. */
  inherited: ResolvedItem[];
  candidates: ReadonlyMap<string, RuleCandidate>;
  selection: Selection;
  canEdit: boolean;
  languages: Languages;
  actions: CanvasActions;
}

type Move = { up?: () => void; down?: () => void };

/** One draggable entry; its handle shows while it is hovered. */
const SortableItem: React.FC<{ id: string; disabled: boolean; children: (handle: React.ReactNode) => React.ReactNode }> = ({
  id, disabled, children,
}) => {
  const { t } = useLocalization();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...sortableHoverStyles, position: 'relative', transform: CSS.Transform.toString(transform), transition,
        ...(isDragging && { opacity: 0.5, zIndex: 2 }),
      }}
    >
      {children(!disabled && (
        <Box component="span" className="drag-handle" title={t('Customization_Designer_DragHint')} sx={handleStyles} {...attributes} {...listeners}>
          <GripVerticalIcon />
        </Box>
      ))}
    </Box>
  );
};

/** One reorderable list. Each has its own drag context, so nothing can be dropped into another list. */
const SortableList: React.FC<{
  ids: string[];
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  children: (id: string, index: number, handle: React.ReactNode) => React.ReactNode;
}> = ({ ids, disabled, onMove, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) onMove(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {ids.map((id, index) => (
          <SortableItem key={id} id={id} disabled={disabled || ids.length < 2}>
            {(handle) => children(id, index, handle)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

/**
 * The form as the device draws it, every field shown (the conditional ones too, since the
 * author has to reach them), with its logic drawn onto it quietly: a follow-up indented under
 * the field that opens it, under the answer that opens it; any other condition as a line of
 * text above the field.
 */
const DesignCanvas: React.FC<Props> = ({ draft, inherited, candidates, selection, canEdit, languages, actions }) => {
  const { t } = useLocalization();

  const { resolve } = languages;

  const [menu, setMenu] = useState<{ at: MenuAnchor; target: MenuTarget } | null>(null);
  const [typeMenu, setTypeMenu] = useState<{ at: MenuAnchor; pick: (type: FormInputType) => void } | null>(null);

  const openMenu = (event: React.MouseEvent, target: MenuTarget) => {
    if (!canEdit) return;

    event.preventDefault();
    event.stopPropagation();
    if (target.kind === 'field' && target.field.id) actions.select({ kind: 'field', id: target.field.id });
    if (target.kind === 'group' && target.group.id) actions.select({ kind: 'group', id: target.group.id });
    setMenu({ at: { point: { top: event.clientY, left: event.clientX } }, target });
  };

  const isSelected = (kind: 'field' | 'group', id?: string) => selection?.kind === kind && selection.id === id;

  /** When an entry not drawn under its trigger is asked, in one line of muted text. */
  const conditionLine = (entry: PoiField | PoiFieldGroup) => {
    const visibility = readVisibility(entry);

    if (visibility.mode === 'always') return null;

    const words = visibility.mode === 'never'
      ? t('Customization_Designer_NeverAsked')
      : visibility.mode === 'when'
        ? t(visibility.show ? 'Customization_Designer_AskedWhen' : 'Customization_Designer_HiddenWhen', [whenWords(visibility, candidates, resolve)])
        : (entry.rules ?? []).map((rule) => ruleWords(rule, candidates, resolve)).join(' · ');

    return <Box sx={conditionLineStyles}>{words}</Box>;
  };

  const warnings = (entry: PoiField | PoiFieldGroup) =>
    entryWarnings(entry, candidates).map((warning) => <Box key={warning} sx={warningLineStyles}>{warning}</Box>);

  /** Icon buttons at the end of an entry's first row, shown while it is hovered or selected. */
  const entryActions = (items: { title: string; icon: React.ReactNode; onClick: (element: HTMLElement) => void }[]) => (
    <Stack direction="row" className="designer-actions" sx={cardActionsStyles} onClick={(event) => event.stopPropagation()}>
      {items.map((item) => (
        <Tooltip key={item.title} title={item.title}>
          <IconButton size="small" onClick={(event) => item.onClick(event.currentTarget)}>{item.icon}</IconButton>
        </Tooltip>
      ))}
    </Stack>
  );

  const fieldCard = (field: PoiField, { nested, locked, move }: { nested: boolean; locked: boolean; move?: Move }) => {
    const id = field.id;

    return (
      <Box
        sx={{ ...fieldCardStyles, ...(isSelected('field', id) && fieldCardSelectedStyles), ...(locked && { cursor: 'default' }) }}
        onClick={(event) => {
          event.stopPropagation();
          if (!locked && id) actions.select({ kind: 'field', id });
        }}
        onContextMenu={locked ? undefined : (event) => openMenu(event, { kind: 'field', field, nested, move })}
      >
        {/* Under its trigger, the answer above it already says when. */}
        {!nested && conditionLine(field)}
        <FieldSketch
          field={field}
          languages={languages}
          actions={canEdit && !locked && id && entryActions([
            ...(followUpAnswers(field).length > 0
              ? [{
                title: t('Customization_Designer_AddFollowUp'),
                icon: <CodeBranchIcon />,
                onClick: (element: HTMLElement) => setMenu({ at: { element }, target: { kind: 'followUps', field } }),
              }]
              : []),
            { title: t('Customization_Duplicate'), icon: <CopyIcon />, onClick: () => actions.duplicate(id) },
            { title: t('Common_Delete'), icon: <TrashIcon />, onClick: () => actions.remove(id) },
          ])}
        />
        {warnings(field)}
      </Box>
    );
  };

  /** A field and, indented under it, its follow-ups in runs that share an answer. */
  const fieldNode = (node: FieldNode, nested: boolean, locked: boolean, move?: Move): React.ReactNode => {
    const branches: { label: string; nodes: FieldNode[] }[] = [];

    for (const child of node.children) {
      const visibility = readVisibility(child.field);
      const label = visibility.mode === 'when' ? branchWords(visibility, candidates, resolve) : '';
      const last = branches[branches.length - 1];

      if (last?.label === label) last.nodes.push(child);
      else branches.push({ label, nodes: [child] });
    }

    return (
      <Box key={node.field.id}>
        {fieldCard(node.field, { nested, locked, move })}
        {branches.length > 0 && (
          <Box sx={branchStyles}>
            {branches.map((branch, index) => (
              <Box key={index}>
                <Box component="span" sx={branchLabelStyles}>{branch.label}</Box>
                {branch.nodes.map((child) => fieldNode(child, true, locked))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  /** A list of fields (the type's own, or a group's), reorderable a whole block at a time. */
  const fieldList = (fields: PoiField[], container: Container, locked = false) => {
    const roots = buildTree(fields);

    if (locked) return roots.map((root) => fieldNode(root, false, true));

    return (
      <SortableList
        ids={roots.map((root) => root.field.id ?? String(root.index))}
        disabled={!canEdit}
        onMove={(from, to) => actions.moveBlock(container, from, to)}
      >
        {(_, index, handle) => (
          <>
            {handle}
            {fieldNode(roots[index], false, false, {
              up: index > 0 ? () => actions.moveBlock(container, index, index - 1) : undefined,
              down: index < roots.length - 1 ? () => actions.moveBlock(container, index, index + 1) : undefined,
            })}
          </>
        )}
      </SortableList>
    );
  };

  /** A group as the device heads it: the name, when it is shown, then its fields. */
  const groupSection = (group: PoiFieldGroup, index: number, locked = false) => {
    const id = group.id;
    const addTo = (element: HTMLElement) => setTypeMenu({ at: { element }, pick: (type) => actions.addField(index, type) });

    return (
      <Box>
        <Box
          sx={{ ...groupHeaderStyles, ...(isSelected('group', id) && fieldCardSelectedStyles), ...(locked && { cursor: 'default' }) }}
          onClick={(event) => {
            event.stopPropagation();
            if (!locked && id) actions.select({ kind: 'group', id });
          }}
          onContextMenu={locked ? undefined : (event) => openMenu(event, { kind: 'group', group, index })}
        >
          <Stack direction="row" alignItems="center" sx={{ minHeight: 28 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, fontWeight: 700 }} noWrap>
              <LabelText text={group.name} empty={t('Customization_NewGroup')} languages={languages} />
            </Typography>
            {canEdit && !locked && id && entryActions([
              { title: t('Customization_Designer_AddFieldTo', [resolve(group.name) || t('Customization_NewGroup')]), icon: <PlusIcon />, onClick: addTo },
              { title: t('Customization_DeleteGroup'), icon: <TrashIcon />, onClick: () => actions.removeGroup(id) },
            ])}
          </Stack>
          <Box sx={{ '& > *': { mb: 0 } }}>{conditionLine(group)}</Box>
          {warnings(group)}
        </Box>

        {fieldList(group.fields, index, locked)}

        {/* An empty group says how to fill it; it is there until a field is, selected or not. */}
        {!locked && group.fields.length === 0 && (
          <Button
            size="small"
            startIcon={<PlusIcon />}
            disabled={!canEdit}
            sx={{ ...addButtonStyles, ml: 0.5 }}
            onClick={(event) => {
              event.stopPropagation();
              addTo(event.currentTarget);
            }}
          >
            {t('Customization_Designer_AddFieldTo', [resolve(group.name) || t('Customization_NewGroup')])}
          </Button>
        )}
      </Box>
    );
  };

  // Inherited content, locked, in one tinted region per ancestor, named once.
  const regions = inherited.reduce<{ name: string; entries: ResolvedItem[] }[]>((all, entry) => {
    const name = resolve(entry.inheritedFrom?.name);
    const last = all[all.length - 1];

    if (last?.name === name) last.entries.push(entry);
    else all.push({ name, entries: [entry] });

    return all;
  }, []);

  return (
    <Box sx={deviceStyles} onClick={() => actions.select(null)} onContextMenu={(event) => openMenu(event, { kind: 'canvas' })}>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1.25, mb: 1 }}>{t('Customization_Designer_BuiltIn')}</Typography>

      {regions.map((region) => (
        <Box key={region.name} sx={inheritedRegionStyles}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.25, pt: 0.5, color: 'primary.light', fontSize: 12 }}>
            <LockIcon /><span>{t('Customization_InheritedFrom', [region.name])}</span>
          </Stack>
          {fieldList(region.entries.flatMap((entry) => (entry.kind === 'field' ? [entry.field] : [])), 'type', true)}
          {region.entries.map((entry) => entry.kind === 'group' && (
            <Box key={entry.group.id}>{groupSection(entry.group, -1, true)}</Box>
          ))}
        </Box>
      ))}

      {draft.fields.length === 0 && draft.groups.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{t('Customization_Designer_EmptyForm')}</Typography>
      )}

      {fieldList(draft.fields, 'type')}

      <SortableList ids={draft.groups.map((group, index) => group.id ?? String(index))} disabled={!canEdit} onMove={actions.moveGroup}>
        {(_, index, handle) => (
          <>
            {handle}
            {groupSection(draft.groups[index], index)}
          </>
        )}
      </SortableList>

      <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} onClick={(event) => event.stopPropagation()}>
        <Button
          size="small"
          startIcon={<PlusIcon />}
          disabled={!canEdit}
          sx={addButtonStyles}
          onClick={(event) => setTypeMenu({ at: { element: event.currentTarget }, pick: (type) => actions.addField('type', type) })}
        >
          {t('Customization_AddField')}
        </Button>
        <Tooltip title={draft.groups.length ? t('Customization_Designer_OrderNote') : ''}>
          <span>
            <Button size="small" startIcon={<PlusIcon />} disabled={!canEdit} sx={addButtonStyles} onClick={actions.addGroup}>
              {t('Customization_AddGroup')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {menu && (
        <ActionMenu
          at={menu.at}
          target={menu.target}
          actions={actions}
          candidates={candidates}
          resolve={resolve}
          onPickType={(pick) => setTypeMenu({ at: menu.at, pick })}
          onClose={() => setMenu(null)}
        />
      )}
      <TypeMenu at={typeMenu?.at ?? null} onPick={(type) => typeMenu?.pick(type)} onClose={() => setTypeMenu(null)} />
    </Box>
  );
};

export default DesignCanvas;
